import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import {
  appendCheckpoint,
  cancelTracking,
  finishTracking,
  getActiveTrackings,
  recordSessionFlowIntent,
  setCaptureTransport,
  startTracking,
  TrackingKey,
  type CaptureTransport,
} from '@heidi/runtime/performance';

export type RecordingControlTrackingKey =
  | typeof TrackingKey.SCRIBE_PAUSE_RECORDING
  | typeof TrackingKey.SCRIBE_STOP_RECORDING;

/** The pause or stop attempt one control press opened. */
export type RecordingControlTracking = {
  readonly trackingKey: RecordingControlTrackingKey;
  readonly trackingInstanceId: number;
};

export type RecordingControlOptions = {
  tracking?: RecordingControlTracking;
};

/** Where the press came from. A closed set, so RUM can split coverage by surface. */
export type RecordingControlSurface =
  | 'recording_button'
  | 'mini_mode'
  | 'nav_indicator'
  | 'session_list'
  | 'projects_overlay'
  | 'demo_flow';

const SUPERSEDED_ERROR_BY_KEY = {
  [TrackingKey.SCRIBE_PAUSE_RECORDING]: {
    name: 'PauseRecordingSuperseded',
    message: 'Pause attempt superseded by an explicit retry',
  },
  [TrackingKey.SCRIBE_STOP_RECORDING]: {
    name: 'StopRecordingSuperseded',
    message: 'Stop attempt superseded by an explicit retry',
  },
} as const;

/**
 * Opens a pause or stop attempt and records its intent. A retry cancels the
 * attempt still open as superseded. Best-effort: a telemetry failure never
 * blocks the control, and returns no handle unless the attempt opened.
 */
export const openRecordingControlAttempt = (
  trackingKey: RecordingControlTrackingKey,
  getMetadata: () => Record<string, unknown>
): RecordingControlTracking | undefined => {
  let tracking: RecordingControlTracking | undefined;
  try {
    // Read first: a throw here must leave the open attempt untouched.
    const metadata = getMetadata();
    if (getActiveTrackings().has(trackingKey)) {
      const superseded = SUPERSEDED_ERROR_BY_KEY[trackingKey];
      finishTracking({
        key: trackingKey,
        outcome: 'failed',
        error: createSessionFlowPreflightError(superseded.name, superseded.message),
        metadata: { preflight_gate: 'superseded_by_retry' },
      });
    }
    startTracking({ key: trackingKey, metadata });
    const trackingInstanceId = getActiveTrackings().get(trackingKey)?.instanceId;
    if (trackingInstanceId !== undefined) tracking = { trackingKey, trackingInstanceId };
    // After the open, so no intent lacks an attempt.
    recordSessionFlowIntent(trackingKey);
  } catch (err) {
    logger.warn('[recording-control] attempt telemetry start failed', {
      trackingKey,
      errorName: err instanceof Error ? err.name : typeof err,
    });
  }
  return tracking;
};

/** Owns the attempt only while it is still the active instance for its key. */
const ownsRecordingControlAttempt = (
  tracking: RecordingControlTracking | undefined
): tracking is RecordingControlTracking =>
  tracking !== undefined &&
  getActiveTrackings().get(tracking.trackingKey)?.instanceId ===
    tracking.trackingInstanceId;

export const appendRecordingControlCheckpoint = (
  tracking: RecordingControlTracking | undefined,
  checkpointName: string
): void => {
  if (!ownsRecordingControlAttempt(tracking)) return;
  appendCheckpoint({ key: tracking.trackingKey, checkpointName });
};

/** Tags a pause or stop attempt with the transport carrying it out; no-op once no longer owned. */
export const setRecordingControlTransport = (
  tracking: RecordingControlTracking | undefined,
  transport: CaptureTransport
): void => {
  if (!ownsRecordingControlAttempt(tracking)) return;
  setCaptureTransport({ key: tracking.trackingKey, transport });
};

/** The transport of a recording this client captures itself, not a remote device. */
export const localCaptureTransport = (): CaptureTransport =>
  isTauri() ? 'desktop_mic' : 'web';

export const finishRecordingControlAttempt = (
  tracking: RecordingControlTracking | undefined,
  params: { metadata?: Record<string, unknown> } & (
    | { outcome?: never; error?: never }
    | { outcome: 'failed'; error: unknown }
  ) = {}
): void => {
  if (!ownsRecordingControlAttempt(tracking)) return;
  if (params.outcome === 'failed') {
    finishTracking({
      key: tracking.trackingKey,
      outcome: 'failed',
      error: params.error,
      metadata: params.metadata,
    });
    return;
  }
  finishTracking({ key: tracking.trackingKey, metadata: params.metadata });
};

export const cancelRecordingControlAttempt = (
  tracking: RecordingControlTracking | undefined
): void => {
  if (!ownsRecordingControlAttempt(tracking)) return;
  cancelTracking({ key: tracking.trackingKey });
};
